
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl'
import type { BoundingBox } from '../../lib/geo'
import { CoordinateGridFallback } from './CoordinateGridFallback'
import type { GridLine, GridPoint, GridPolygon } from './CoordinateGridFallback'
import { LAYERS, LAYERS_3D, SRC, SOURCES } from './layers'
import { geofenceToFeature, routeToFeature } from './layers'
import { disableTerrain, enableTerrain } from './terrain'
import type { Feature, Point, Polygon, LineString } from 'geojson'

/* ============================================================================
 * КАРТА ИНДИКАТОРА ВОЗДУШНОЙ ОБСТАНОВКИ
 *
 * Основной режим — MapLibre GL. Если стиль или тайлы недостижимы (офлайн,
 * закрытая сеть, нет тайл-сервера), компонент ПЛАВНО деградирует до
 * координатной сетки WGS-84. Пустой серый блок не показывается никогда.
 *
 * Подготовленные слои: районы заданий, геозоны, декларации, кооперативные
 * цели (ADS-B / Remote ID), некооперативные цели (РЛС / RF), нарушители.
 * ========================================================================= */

/** Стиль по умолчанию — открытая подложка. Переопределяется VITE_MAP_STYLE_URL. */
/**
 * Базовая подложка карты (задача №7).
 *
 * БЫЛО: demotiles.maplibre.org — это учебный стиль: он рисует несколько
 * стран блоками и НЕ содержит ни рельефа, ни нормальной подписи. Для
 * ситуационного центра это выглядит как «карта сломалась».
 *
 * СТАЛО: тёмная подложка реального мира. Дизайн-решение консоли ИВО —
 * тёмная #0A1120 (ситуационный центр), поэтому берём тёмный стиль.
 * OpenFreeMap — бесплатный, без ключа и без ограничений по домену.
 * Переопределяется VITE_MAP_STYLE_URL (для закрытого контура — свой тайл-сервер).
 */
const DEFAULT_STYLE = 'https://tiles.openfreemap.org/styles/dark'

/**
 * Запасные стили в порядке предпочтения.
 * Первый недоступен — пробуем следующий, и только потом уходит в координатную
 * сетку: сетка это аварийный режим, а не норма.
 */
const FALLBACK_STYLES: readonly string[] = [
  'https://tiles.openfreemap.org/styles/dark',
  'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
]

/** Таймаут проверки доступности стиля, мс */
const STYLE_PROBE_TIMEOUT_MS = 3500

/** Наклон камеры в 3D-режиме, градусы. 60 — предел MapLibre в WebGL */
export const PITCH_3D = 55

/** Азимут в 3D-режиме: чуть с северо-востока, чтобы читалась грань объёма */
export const BEARING_3D = -18

/**
 * Включить или выключить 3D-режим на карте.
 *
 * Что делает:
 *   1. наклоняет камеру (pitch) и ставит азимут — иначе объёмные зоны видны
 *      сверху и выглядят плоской заливкой: без наклона extrusion не читается;
 *   2. показывает слои 3D и прячет плоские заливки тех же полигонов
 *      (иначе плоскость перекрывает объём снизу и «съедает» его);
 *   3. экстрагирует/сжимает зум на единицу, чтобы при наклоне в кадр влезла
 *      высота, а не только проекция площади.
 *
 * Вынесено отдельной функцией, а не инлайн в обработчике: этим же вызывается
 * применить3d при 'load' и при смене prop — иначе эти места разъезжаются.
 */
interface Bbox3d { lat1: number; lon1: number; lat2: number; lon2: number }
function apply3dTo(map: MapLibreMap, on: boolean, target3d?: Bbox3d | null) {
  if (!map.isStyleLoaded?.()) return
  const flatIds = ['geofences-fill', 'missions-fill', 'declarations-fill']
  const solidIds = ['geofences-outline', 'missions-outline', 'declarations-outline']
  const volIds = LAYERS_3D.map((l) => l.id)
  const setVis = (ids: string[], vis: 'visible' | 'none') => {
    for (const id of ids) {
      // Слоя может не быть (например, стиль не успел) — это не ошибка
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis)
    }
  }

  try {
    setVis(volIds, on ? 'visible' : 'none')
    // Плоскую заливку в 3D убираем: объём её заменяет. Контур оставляем —
    // он читается как граница зоны и в 3D тоже.
    setVis(flatIds, on ? 'none' : 'visible')
    setVis(solidIds, 'visible')

    // ⚠️ МОБИЛЬНЫЙ ФРИЗ (владелец, телефон 2026-10-06, повторно): точки и
    // контур зоны появились, но при 3D карта замерзает наглухо. Headless это
    // НЕ воспроизводит (мышь ≠ touch). Известные триггеры MapLibre 4.x на
    // тач-устройствах: (A) анимация камеры одновременно с setTerrain;
    // (B) fitBounds с pitch; (C) touch-жесты во время анимации.
    //
    // ФИКС — три независимые защиты:
    //   1. setTerrain СРАЗУ и БЕЗ параллельной анимации;
    //   2. камера: jumpTo (мгновенно, без анимации) на центр зоны, затем
    //      easeTo ТОЛЬКО pitch/bearing — короткая анимация без пересчёта
    //      center/zoom (transform не меняется);
    //   3. на время анимации (800 мс) тач-жесты отключены, на moveend
    //      включаются обратно.
    const cam = map as unknown as {
      jumpTo?: (o: Record<string, unknown>) => void
      touchZoomRotate?: { disable: () => void; enable: () => void }
      dragPan?: { disable: () => void; enable: () => void }
    }
    // (1) рельеф сразу, вне анимации
    if (on) void enableTerrain(map)
    else disableTerrain(map)

    if (on) {
      // (2а) мгновенный центр на зону: без анимации = без фриза
      const target = target3d
      if (target) {
        const clat = (target.lat1 + target.lat2) / 2
        const clon = (target.lon1 + target.lon2) / 2
        cam.jumpTo?.({ center: [clon, clat], zoom: 13.5, pitch: 0, bearing: 0 })
      }
      // ⚠️ БЛОКИРОВКУ ЖЕСТОВ УБРАЛИ (владелец, телефон 2026-10-06, 3-я
      // итерация: карта отрисована, цели видны, НО двигать нельзя). Причина:
      // moveend после easeTo(pitch) с terrain на тач-устройстве НЕ НАСТУПАЕТ
      // (MapLibre 4.7.1 issue) -> enable() никогда не вызывался -> жесты
      // залипали disabled навсегда. Тач во время короткой анимации теперь
      // просто прерывает её — это штатное поведение MapLibre.
      map.easeTo({ pitch: PITCH_3D, bearing: BEARING_3D, duration: 550 })
      // Страховка от залипания любых жестов: через 1.2 с принудительно
      // включаем (idempotent — enable на включённых жестах безвреден).
      window.setTimeout(() => {
        try {
          map.touchZoomRotate?.enable()
          map.dragPan?.enable()
        } catch { /* ignore */ }
      }, 1200)
    } else {
      map.easeTo({ pitch: 0, bearing: 0, duration: 450 })
    }

    // ⚠️ ИНСТРУМЕНТ ДИАГНОСТИКИ, а не украшение.
    // По коду страницы нельзя понять, применился ли наклон: canvas при 2D и 3D
    // выглядит одинаково, и предыдущая проверка это пропустила — скриншот
    // показал плоскую карту при «активной» кнопке. Поэтому пишем фактическое
    // состояние камеры в data-атрибуты контейнера: их читают и CSS, и тесты.
    // Пишем на 'moveend', потому что easeTo анимирует и сразу после вызова
    // pitch ещё старый.
    const stamp = () => {
      const el = map.getCanvasContainer()
      if (!el) return
      el.setAttribute('data-3d', on ? 'on' : 'off')
      el.setAttribute('data-pitch', String(Math.round(map.getPitch())))
      el.setAttribute('data-bearing', String(Math.round(map.getBearing())))
    }
    map.once('moveend', stamp)
    stamp()

    // Рельеф включён ВЫШЕ, до easeTo (порядок критичен для мобильных).
  } catch {
    // setLayoutProperty может бросить, если слой удалён стилем. Не роняем карту.
  }
}



/** bbox всех геозон для прицела 3D-камеры (AGL-зоны приоритетны) */
function compute3dTarget(
  polys: GridPolygon[],
): Bbox3d | null {
  const withAlt = polys.filter(
    (p) => p.kind === 'geofence' && Array.isArray(p.points) && p.points.length >= 3,
  )
  if (!withAlt.length) return null
  let lat1 = 90, lon1 = 180, lat2 = -90, lon2 = -180
  for (const p of withAlt) {
    for (const [la, lo] of p.points) {
      if (la < lat1) lat1 = la
      if (la > lat2) lat2 = la
      if (lo < lon1) lon1 = lo
      if (lo > lon2) lon2 = lo
    }
  }
  // немного расширить, чтобы зона не впритык
  const dLat = Math.max((lat2 - lat1) * 0.35, 0.004)
  const dLon = Math.max((lon2 - lon1) * 0.35, 0.005)
  return { lat1: lat1 - dLat, lon1: lon1 - dLon, lat2: lat2 + dLat, lon2: lon2 + dLon }
}

export type MapStatus = 'loading' | 'ready' | 'fallback'

export interface MapViewProps {
  bbox: BoundingBox
  /** Цели: кооперативные, некооперативные, нарушители, свой БВС, суда */
  points?: GridPoint[]
  /** Маршруты и линии (декларации, векторы РЛС) */
  lines?: GridLine[]
  /** Районы и геозоны */
  polygons?: GridPolygon[]
  /** Цели для нанесения в режиме MapLibre (те же данные в GeoJSON) */
  geoPoints?: Feature<Point>[]
  /** Хвосты траекторий (FR24) — GeoJSON-линии */
  trailFeatures?: Feature<LineString>[]
  /** Прогнозные дуги 10 с — GeoJSON-линии */
  predict10Features?: Feature<LineString>[]
  /** Прогнозные дуги 60 с — GeoJSON-линии */
  predict60Features?: Feature<LineString>[]
  /** Оверлей двойника (D3): reality-хвосты (фактические траектории) */
  overlayRealFeatures?: Feature<LineString>[]
  /** Оверлей двойника (D3): twin-прогноз бортов (пунктир) */
  overlayTwinFeatures?: Feature<LineString>[]
  /** Захват клика по карте — координаты выбранной точки */
  onPick?: (lat: number, lon: number) => void
  /**
   * Клик по конфликтной дуге прогноза (карточка пары, 2026-10-07g).
   * Слой известен MapView (predict10-lines / predict60-lines), поэтому
   * перехват клика живёт здесь, а не в App: queryRenderedFeatures по
   * клику решает, попал ли курсор в дугу, и отдаёт callsign борта.
   * null = клик мимо дуг (карточку надо закрыть).
   */
  onConflictArcClick?: (drone: string | null) => void
  /**
   * Карточка конфликтной пары — оверлей в правом нижнем углу карты.
   * Контент строит App (там данные /api/trajectories), MapView только
   * позиционирует: absolute снизу справа, поверх канвы, под легендой.
   */
  conflictCard?: ReactNode
  /** Зафиксировать цель (собственный БВС) для инструмента «Дальность и азимут» */
  className?: string
  /**
   * Режим 3D (задача №14): наклон камеры + объёмные геозоны и районы.
   * Управляется сверху — кнопка «3D» в панели карты.
   */
  is3d?: boolean
}

export function MapView({
  bbox,
  points = [],
  lines = [],
  polygons = [],
  geoPoints,
  trailFeatures,
  predict10Features,
  predict60Features,
  overlayRealFeatures,
  overlayTwinFeatures,
  onPick,
  onConflictArcClick,
  conflictCard,
  className = '',
  is3d = false,
}: MapViewProps) {
  const [status, setStatus] = useState<MapStatus>('loading')
  const [reason, setReason] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapLibreMap | null>(null)

  const styleUrl = useMemo(() => {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    return env?.VITE_MAP_STYLE_URL || DEFAULT_STYLE
  }, [])

  /**
   * Цепочка кандидатов: явный VITE_MAP_STYLE_URL имеет приоритет и идёт
   * первым; за ним — публичные тёмные подложки. Пустой массив невозможен,
   * потому что DEFAULT_STYLE всегда присутствует в FALLBACK_STYLES.
   */
  const styleCandidates = useMemo<string[]>(() => {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    const explicit = env?.VITE_MAP_STYLE_URL
    if (explicit) return [explicit]
    return [...FALLBACK_STYLES]
  }, [])

  // Стиль, который реально загрузился: может отличаться от запрошенного,
  // если первый в цепочке оказался недоступен.
  const [activeStyle, setActiveStyle] = useState<string>(styleUrl)

  const center = useMemo<BoundingBox>(
    () => ({
      lat1: bbox.lat1,
      lon1: bbox.lon1,
      lat2: bbox.lat2,
      lon2: bbox.lon2,
    }),
    [bbox.lat1, bbox.lon1, bbox.lat2, bbox.lon2],
  )

  /* ------------------------------------------------------------------
   * 1. Проверка достижимости стиля. Если недоступен — сразу заглушка,
   *    не создаём карту вовсе (быстрее и не мигает серым).
   * ------------------------------------------------------------------ */
  useEffect(() => {
    let cancelled = false

    // Последовательно проверяем кандидатов. Реальная карта поднимается только
    // когда НАЙДЕН доступный стиль, поэтому пользователь не видит серый блок.
    const probe = async (): Promise<void> => {
      const failures: string[] = []
      for (const candidate of styleCandidates) {
        const controller = new AbortController()
        const timer = globalThis.setTimeout(
          () => controller.abort(),
          STYLE_PROBE_TIMEOUT_MS,
        )
        try {
          const res = await fetch(candidate, {
            signal: controller.signal,
            mode: 'cors',
            cache: 'no-cache',
          })
          if (cancelled) return
          if (res.ok) {
            setActiveStyle(candidate)
            setReason(
              candidate === styleCandidates[0]
                ? null
                : `основной источник недоступен, работает ${new URL(candidate).host}`,
            )
            setStatus('loading')
            return
          }
          failures.push(`${new URL(candidate).host}: HTTP ${res.status}`)
        } catch {
          if (cancelled) return
          failures.push(`${new URL(candidate).host}: таймаут/сеть`)
        } finally {
          globalThis.clearTimeout(timer)
        }
      }
      // Ни один стиль не доступен — аварийная координатная сетка.
      setReason(
        failures.length
          ? `подложка недоступна (${failures.join('; ')})`
          : 'подложка не настроена',
      )
      setStatus('fallback')
    }

    void probe()
    return () => {
      cancelled = true
    }
  }, [styleCandidates, styleUrl])

  /* ------------------------------------------------------------------
   * 2. Создание карты MapLibre и добавление подготовленных слоёв.
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (status === 'fallback') return
    const container = containerRef.current
    if (!container || mapRef.current) return

    let map: MapLibreMap
    try {
      map = new maplibregl.Map({
        container,
        // Именно проверенный стиль: иначе карта уйдёт в fallback уже по факту
        // сетевой ошибки, хотя запасной источник был доступен.
        style: activeStyle,
        center: [(center.lon1 + center.lon2) / 2, (center.lat1 + center.lat2) / 2],
        zoom: 10,
        attributionControl: { compact: true },
        // 3D-режим (задача №14): объёмные геозоны и районы.
        // Раньше здесь стояло dragRotate:false / pitchWithRotate:false — вращение
        // и наклон были ЯВНО запрещены, поэтому объёмные зоны было негде увидеть.
        // Теперь наклон разрешён (до 70°) и включается по кнопке «3D»;
        // вращение оставлено выключенным — для ИВО достаточно наклона.
        pitchWithRotate: true,
        dragRotate: false,
        // MapLibre отклоняет pitch выше 60° в WebGL; 70 с запасом, но
        // фактический наклон ограничим сами в apply3d (см. PITCH_3D).
        maxPitch: 70,
        // Тайлы, которых нет, не должны «висеть» — показываем заглушку
        maxTileCacheSize: 128,
      })
    } catch (err) {
      setReason(err instanceof Error ? err.message : 'ошибка инициализации')
      setStatus('fallback')
      return
    }

    mapRef.current = map
    let settled = false

    // ⚠️ ТЕСТОВЫЙ ХУК, а не «просто удобно».
    // Три попытки проверить 3D дали три разных ответа, потому что состояние
    // камеры в WebGL не читается из DOM: canvas одинаков при 2D и 3D, а
    // производные признаки (transform/perspective) врали. Единственный честный
    // источник — сам экземпляр карты. Поэтому выкладываем его в
    // window.__utmMap только когда VITE_IVO_TEST=1, чтобы в обычной сборке
    // internals наружу не торчали.
    if ((import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_IVO_TEST === '1') {
      ;(window as unknown as Record<string, unknown>).__utmMap = map
    }

    // Ошибка загрузки стиля/тайлов после старта — тоже деградация
    map.on('error', (e) => {
      if (settled) return
      const msg = e?.error?.message ?? 'ошибка загрузки тайлов'
      setReason(msg)
      setStatus('fallback')
    })

    map.on('load', () => {
      settled = true
      try {
        // Источники GeoJSON
        for (const [id, src] of Object.entries(SOURCES)) {
          map.addSource(id, src)
        }
        // Подготовленные слои в правильном порядке отрисовки
        for (const layer of LAYERS) {
          map.addLayer(layer)
        }
        // 3D-объём (задача №14). Добавляем ВСЕГДА, но показываем только при
        // включённом режиме: иначе flat fill и extrusion дрались бы за одну
        // геометрию и плоскость перекрывала бы объём.
        for (const layer of LAYERS_3D) {
          map.addLayer(layer)
          // По умолчанию скрыты — включаются из apply3d()
          map.setLayoutProperty(layer.id, 'visibility', 'none')
        }
        apply3dTo(map, is3d, compute3dTarget(polygons))
        setStatus('ready')
      } catch (err) {
        setReason(err instanceof Error ? err.message : 'ошибка добавления слоёв')
        setStatus('fallback')
      }
    })

    // Страховка: если load не наступил за 8 секунд — деградируем
    const watchdog = globalThis.setTimeout(() => {
      if (!settled) {
        setReason('превышено время загрузки стиля')
        setStatus('fallback')
      }
    }, 8000)

    return () => {
      globalThis.clearTimeout(watchdog)
      map.remove()
      mapRef.current = null
    }
  }, [status, activeStyle, center.lat1, center.lon1, center.lat2, center.lon2])

  /* ------------------------------------------------------------------
   * 3-бис. Переключение 3D-режима (задача №14).
   *
   * Отдельный эффект, а не только в обработчике кнопки: к этому месту мы
   * возвращаемся и при первой отрисовке, когда стиля ещё нет (тогда apply3dTo
   * выйдет по isStyleLoaded, а при 'load' режим применится повторно).
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const map = mapRef.current
    if (status !== 'ready' || !map) return
    apply3dTo(map, is3d, compute3dTarget(polygons))
  }, [is3d, status])

  /* ------------------------------------------------------------------
   * 3-втор. ПОЛИГОНЫ И ЛИНИИ В ИСТОЧНИКИ MAPLIBRE (задача №14).
   *
   * ⚠️ Здесь была дыра, найденная при переходе к 3D: geofenceToFeature() и
   * routeToFeature() в layers.ts НИКОГДА НЕ ВЫЗЫВАЛИСЬ. Источники
   * SRC.geofences / SRC.missions / SRC.declarations создавались пустыми и
   * оставались пустыми — то есть слои геозонов, районов и маршрутов на
   * «настоящей» карте не рисовались вообще. Геозоны были видны только в
   * координатной сетке (режим деградации), то есть чем лучше работала карта,
   тем меньше геозонов было видно. Именно поэтому и «не видно геофенсов».
   *
   * Теперь полигоны зон и линии маршрутов идут в свои источники, причём с
   * полосой высот (lowerLimit/upperLimit) — это то, что делает зону объёмной.
   * ------------------------------------------------------------------ */
  const geoPolyFeatures = useMemo<Feature<Polygon>[]>(
    () =>
      polygons
        .map((p) =>
          geofenceToFeature(
            {
              id: p.id,
              name: p.label,
              polygon: p.points,
              lower_limit: p.lowerLimit,
              upper_limit: p.upperLimit,
              altitude_ref: p.altitudeRef,
              ground_min: p.groundMin,
              ground_max: p.groundMax,
            },
            0,
          ),
        )
        .filter((f): f is Feature<Polygon> => f !== null),
    [polygons],
  )

  /** Зоны (geofence) и районы (mission) разводим по разным источникам */
  const polyByKind = useMemo(() => {
    const byId = new Map(geoPolyFeatures.map((f) => [String(f.properties?.id ?? ''), f]))
    return {
      geofences: polygons
        .filter((p) => p.kind !== 'mission')
        .map((p) => byId.get(p.id))
        .filter((f): f is Feature<Polygon> => Boolean(f)),
      missions: polygons
        .filter((p) => p.kind === 'mission')
        .map((p) => byId.get(p.id))
        .filter((f): f is Feature<Polygon> => Boolean(f)),
    }
  }, [polygons, geoPolyFeatures])

  const geoLineFeatures = useMemo<Feature<LineString>[]>(
    () =>
      lines
        .map((l) =>
          routeToFeature(l.points, { id: l.id, color: l.color, dashed: l.dashed ?? false }),
        )
        .filter((f): f is Feature<LineString> => f !== null),
    [lines],
  )

  useEffect(() => {
    const map = mapRef.current
    if (status !== 'ready' || !map) return
    const put = (srcId: string, features: unknown[]) => {
      const src = map.getSource(srcId) as { setData?: (d: unknown) => void } | undefined
      if (src?.setData) src.setData({ type: 'FeatureCollection', features })
    }
    // Зоны -> SRC.geofences (объёмные), районы заявок -> SRC.missions.
    put(SRC.geofences, polyByKind.geofences)
    put(SRC.missions, polyByKind.missions)
    put(SRC.declarations, geoLineFeatures)
  }, [status, polyByKind, geoLineFeatures])

  /* ------------------------------------------------------------------
   * 3. Обновление данных в источниках MapLibre.
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const map = mapRef.current
    if (status !== 'ready' || !map || !geoPoints) return
    const src = map.getSource(SRC.cooperative) as { setData?: (d: unknown) => void } | undefined
    if (src?.setData) {
      src.setData({ type: 'FeatureCollection', features: geoPoints })
    }
  }, [status, geoPoints])

  /* Хвосты + прогнозные дуги (FR24-стиль, 2026-10-07): заливка в свои
   * источники. Пустой список = setData с пустым FC — слои честно гаснут. */
  useEffect(() => {
    const map = mapRef.current
    if (status !== 'ready' || !map) return
    const put = (srcId: string, features: Feature<LineString>[]) => {
      const s = map.getSource(srcId) as { setData?: (d: unknown) => void } | undefined
      if (s?.setData) s.setData({ type: 'FeatureCollection', features })
    }
    put(SRC.trails, trailFeatures ?? [])
    put(SRC.predict10, predict10Features ?? [])
    put(SRC.predict60, predict60Features ?? [])
    // Оверлей двойника (D3): reality vs twin — свои источники
    put(SRC.overlayReal, overlayRealFeatures ?? [])
    put(SRC.overlayTwin, overlayTwinFeatures ?? [])
  }, [status, trailFeatures, predict10Features, predict60Features,
      overlayRealFeatures, overlayTwinFeatures])

  /* ------------------------------------------------------------------
   * 4. Обработка клика — выдаёт координаты для выбора цели оператором.
   * ------------------------------------------------------------------ */
  /* КЛИК ПО КАРТЕ (единый обработчик, 2026-10-07g):
   * 1) queryRenderedFeatures в точке клика по слоям predict: попали в фичу
   *    с conflict=1 — отдаём её callsign наверх (App покажет карточку пары)
   *    и НЕ смещаем bbox — открытие карточки не должно дёргать карту;
   * 2) мимо дуг — onConflictArcClick(null) (закрыть карточку, если была)
   *    и штатный onPick (смещение интересующей области). */
  useEffect(() => {
    const map = mapRef.current
    if (!map || (!onPick && !onConflictArcClick)) return
    const handler = (e: maplibregl.MapMouseEvent): void => {
      let hit: string | null = null
      if (onConflictArcClick) {
        try {
          const feats = map.queryRenderedFeatures(e.point, {
            layers: ['predict10-lines', 'predict60-lines'],
          })
          for (const f of feats) {
            if (f.properties?.conflict === 1 && f.properties?.drone) {
              hit = String(f.properties.drone)
              break
            }
          }
        } catch {
          hit = null
        }
        onConflictArcClick(hit)
        if (hit !== null) return // клик по дуге — карту не двигаем
      }
      onPick?.(e.lngLat.lat, e.lngLat.lng)
    }
    map.on('click', handler)
    return () => {
      map.off('click', handler)
    }
  }, [status, onPick, onConflictArcClick])

  // Подгонка вида под область интереса
  useEffect(() => {
    const map = mapRef.current
    if (status !== 'ready' || !map) return
    try {
      map.fitBounds(
        [
          [center.lon1, center.lat1],
          [center.lon2, center.lat2],
        ],
        { padding: 40, duration: 400 },
      )
    } catch {
      // Недостаточная область — игнорируем
    }
  }, [status, center.lat1, center.lon1, center.lat2, center.lon2])

  if (status === 'fallback') {
    // ДЕГРАДАЦИЯ: координатная сетка вместо пустого серого блока
    return (
      <CoordinateGridFallback
        box={bbox}
        points={points}
        lines={lines}
        polygons={polygons}
        reason={reason ?? undefined}
      />
    )
  }

  return (
    <div className={`relative h-full w-full overflow-hidden ${className}`}>
      <div ref={containerRef} className="absolute inset-0" data-map-status={status} />
      {conflictCard && (
        <div className="absolute bottom-12 right-2 z-20 max-w-[280px]">{conflictCard}</div>
      )}
      {status === 'loading' && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-center gap-2 bg-surface/80 py-1 text-[11px] text-ink-muted">
          <span className="ut-spin inline-block h-2.5 w-2.5 rounded-full border-2 border-line border-t-accent" />
          Загрузка карты…
        </div>
      )}
    </div>
  )
}
